import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listPlugins, deletePlugin, revalidateAllPlugins, isRegistryOwnedPlugin } from '../lib/api';
import type { BatchRevalidationResponse } from '../lib/api';
import type { Plugin, Pagination } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { TableSkeleton, EmptyState } from '../components/LoadingState';
import DeletionConfirmDialog from '../components/DeletionConfirmDialog';

const CAT_CLASS: Record<string, string> = {
  provider: 'badge--provider', analyzer: 'badge--analyzer',
  condition: 'badge--condition', hook: 'badge--hook', updater: 'badge--updater',
  generator: 'badge--generator', packager: 'badge--packager',
  publisher: 'badge--publisher',
};

export default function PluginsPage() {
  const { user } = useCurrentUser();
  const isAdmin                       = user?.isAdmin ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const initialSort = (searchParams.get('sort') as 'name' | 'downloads' | 'views' | null) ?? 'name';
  const initialOrder = (searchParams.get('order') as 'asc' | 'desc' | null) ?? 'asc';
  const [plugins, setPlugins]         = useState<Plugin[]>([]);
  const [pagination, setPagination]   = useState<Pagination | null>(null);
  const [search, setSearch]           = useState(searchParams.get('search') ?? '');
  const [category, setCategory]       = useState(searchParams.get('category') ?? '');
  const [sort, setSort]               = useState<'name' | 'downloads' | 'views'>(initialSort);
  const [order, setOrder]             = useState<'asc' | 'desc'>(initialOrder);
  const [page, setPage]               = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [revalidating, setRevalidating] = useState(false);
  const [revalidation, setRevalidation] = useState<BatchRevalidationResponse | null>(null);
  const [revalidationError, setRevalidationError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Plugin | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const navigate                      = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (sort !== 'name') params.set('sort', sort);
    if (order !== 'asc') params.set('order', order);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [search, category, sort, order, page, setSearchParams]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    const author = isAdmin ? undefined : (user.login || undefined);
    listPlugins({
      page,
      limit: 25,
      search: search || undefined,
      category: category || undefined,
      author,
      sort,
      order,
    })
      .then((res) => {
        if (cancelled) return;
        setPlugins(res.data ?? []);
        setPagination(res.pagination ?? null);
        setError('');
        setLoading(false);
      })
      .catch((e: unknown) => { if (!cancelled) { setError(e instanceof Error ? e.message : 'Failed'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [page, search, category, sort, order, user, isAdmin, refreshKey]);

  async function handleRevalidateAll() {
    setRevalidating(true);
    setRevalidation(null);
    setRevalidationError('');
    try {
      setRevalidation(await revalidateAllPlugins());
      setRefreshKey((key) => key + 1);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to re-check plugins';
      setRevalidationError(message === 'Unauthorized' ? 'Your admin session has expired. Please sign in again.' : `Re-check failed: ${message}`);
    } finally {
      setRevalidating(false);
    }
  }

  function formatPluginName(plugin: Plugin) {
    return plugin.namespace ? `${plugin.namespace}/${plugin.name}` : plugin.name;
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deletePlugin(deleteTarget.id, {
        confirmation: formatPluginName(deleteTarget),
        deleteVersions: true,
      });
      setPlugins((prev) => prev.filter((plugin) => plugin.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  }

  // Non-admins can only edit/delete their own plugins.
  function canEdit(p: Plugin) {
    return isAdmin || (p.author?.toLowerCase() === user?.login?.toLowerCase());
  }

  return (
    <>
      <div className="page__header">
        <h1 className="page__title">{isAdmin ? 'Plugins' : 'My Plugins'}</h1>
        <div className="flex gap-sm">
          {isAdmin && <button type="button" className="btn btn--secondary" onClick={() => { void handleRevalidateAll(); }} disabled={revalidating}>
            {revalidating ? 'Re-checking…' : 'Re-check all'}
          </button>}
          <button type="button" className="btn btn--primary" onClick={() => navigate('/admin/plugins/new')}>+ Add</button>
        </div>
      </div>
      <div className="page__body">
        {!isAdmin && (
          <div className="alert alert--info">
            Community view — you can only manage plugins attributed to <strong>{user?.login}</strong>.{' '}
            <a href="/admin/submit" target="_blank" rel="noopener">
              Submit a new plugin →
            </a>
          </div>
        )}
        <p className="muted text-xs mb-2">
          Destructive plugin deletions require typed confirmation before the existing authenticated API delete request is sent.
        </p>
        {error && <div className="alert alert--error">{error}</div>}
        {revalidationError && <div className="alert alert--error">{revalidationError}</div>}
        {revalidation && (
          <div className={revalidation.summary.failed > 0 ? 'alert alert--error' : 'alert alert--success'}>
            Re-checked {revalidation.summary.processed} of {revalidation.summary.total} plugins:
            {' '}{revalidation.summary.succeeded} completed, {revalidation.summary.failed} failed.
            {revalidation.summary.failed > 0 && (
              <ul className="error-list">
                {revalidation.data.filter((item) => item.error).map((item) => (
                  <li key={item.id}><strong>{item.name}</strong>: {item.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="search-bar mb-2">
          <input type="search" className="search-input" placeholder="Search…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          <select className="select w-160" value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All categories</option>
            {['provider','analyzer','condition','hook','updater','generator','packager','publisher'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="select w-180" value={`${sort}:${order}`}
            onChange={(e) => {
              const [nextSort, nextOrder] = e.target.value.split(':') as ['name' | 'downloads' | 'views', 'asc' | 'desc'];
              setSort(nextSort);
              setOrder(nextOrder);
              setPage(1);
            }}>
            <option value="name:asc">Name A-Z</option>
            <option value="name:desc">Name Z-A</option>
            <option value="downloads:desc">Downloads high-low</option>
            <option value="downloads:asc">Downloads low-high</option>
            <option value="views:desc">Views high-low</option>
            <option value="views:asc">Views low-high</option>
          </select>
        </div>

        {loading ? <TableSkeleton label="Loading plugins" count={6} /> : plugins.length === 0 ? (
          <EmptyState
            title={isAdmin ? 'No plugins in the registry yet' : 'No plugins attributed to your account'}
            action={isAdmin
              ? <Link to="/admin/plugins/new" className="btn btn--primary">Add the first plugin</Link>
              : <Link to="/admin/submit" className="btn btn--primary">Submit a plugin</Link>}
          >
            {isAdmin
              ? 'Plugins appear here once they are published or imported from GitHub.'
              : 'Plugins you submit are listed here, including while they wait for review.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table--stack">
              <thead><tr>
                <th>Name</th><th>Category</th><th>Author</th><th>License</th><th>Latest</th><th>Views</th><th>Downloads</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {plugins.map((p) => (
                  <tr key={p.id}>
                    <td data-label="Name"><strong>{p.namespace ? <span className="muted font-normal">{p.namespace}/</span> : null}{p.name}</strong>
                      {p.description && <div className="muted truncate text-xs max-w-240">{p.description}</div>}
                    </td>
                    <td data-label="Category"><span className={`badge ${CAT_CLASS[p.category] ?? ''}`}>{p.category}</span></td>
                    <td data-label="Author" className="muted">{p.author}</td>
                    <td data-label="License" className="muted">{p.license}</td>
                    <td data-label="Latest">{p.latestVersion ? <code>v{p.latestVersion}</code> : <span className="muted">—</span>}</td>
                    <td data-label="Views">{Number(p.views ?? 0).toLocaleString()}</td>
                    <td data-label="Downloads">{Number(p.downloads ?? 0).toLocaleString()}</td>
                    <td data-label="Status">
                      <span className={`status-pill status-pill--${p.status === 'pending' ? 'pending' : p.status === 'rejected' ? 'rejected' : 'active'}`}>{p.status ?? 'active'}</span>
                      {/* The reason a submission was turned down, where its
                          author will actually see it. */}
                      {p.status === 'rejected' && p.rejectionReason && (
                        <p className="field__error rejection-reason">
                          {p.rejectionReason}
                        </p>
                      )}
                    </td>
                    <td data-label="Actions"><div className="flex gap-sm">
                      {isRegistryOwnedPlugin(p) ? (
                        <>
                          <Link to={`/admin/plugins/${p.id}/versions`} className="btn btn--sm">Versions</Link>
                          <span className="muted text-xs">SQL-managed</span>
                        </>
                      ) : canEdit(p) ? (
                        <>
                          <Link to={`/admin/plugins/${p.id}`} className="btn btn--sm">Edit</Link>
                          <Link to={`/admin/plugins/${p.id}/versions`} className="btn btn--sm">Versions</Link>
                          <button type="button" className="btn btn--sm btn--danger" onClick={() => { setDeleteError(''); setDeleteTarget(p); }}>Del</button>
                        </>
                      ) : (
                        <span className="muted text-xs">read-only</span>
                      )}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="flex gap-sm mt-2 justify-center items-center">
            <button type="button" className="btn btn--sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span className="muted text-xs">Page {pagination.page}/{pagination.pages} ({pagination.total})</span>
            <button type="button" className="btn btn--sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        )}
      </div>
      <DeletionConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget ? `Delete ${formatPluginName(deleteTarget)}?` : 'Delete plugin?'}
        message={deleteTarget
          ? 'This removes the plugin entry from the registry workspace and withdraws every managed version from this admin view.'
          : ''}
        confirmationValue={deleteTarget ? formatPluginName(deleteTarget) : ''}
        confirmationLabel="Plugin name"
        confirmLabel="Delete plugin"
        busyLabel="Deleting plugin…"
        acknowledgement="I understand this removes the plugin and all listed versions from the registry."
        busy={deleteBusy}
        error={deleteError}
        onClose={() => { if (!deleteBusy) { setDeleteError(''); setDeleteTarget(null); } }}
        onConfirm={() => { void handleDelete(); }}
      />
    </>
  );
}
